import React, { useEffect } from 'react';
import { useTranslation } from 'localization';
import { Skeleton } from 'primereact/skeleton';
import ButtonAtom from '../../atoms/Button/ButtonAtom';

import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useTimeframeSidebar, TimeframeItem } from './useTimeframeSidebar';

import './TimeframeSidebarMolecule.scss';

export type { TimeframeItem } from './useTimeframeSidebar';

export interface TimeframeSidebarMoleculeProps {
  /** Sphere whose center drives the Nearby query */
  sphereId: string;
  /** Organization ID for the All-mode query */
  organizationId: string;
  /** Currently selected timeframe ID */
  activeTimeframeId?: string;
  /** Fires when user clicks a timeframe */
  onSelect: (item: TimeframeItem) => void;
  /** External search term (driven by parent's unified search) */
  searchTerm?: string;
  /** Called when an API fetch error occurs */
  onError?: (messageKey: string) => void;
  className?: string;
}

const TimeframeSidebarMolecule: React.FC<TimeframeSidebarMoleculeProps> = ({
  sphereId,
  organizationId,
  activeTimeframeId,
  onSelect,
  searchTerm = '',
  onError,
  className = '',
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const { mode, loading, nearbyEmpty, error, filtered, handleModeChange } =
    useTimeframeSidebar({
      graphClient: apiClients?.graphClient,
      sphereId,
      organizationId,
      searchTerm,
      activeTimeframeId,
      onSelect,
    });

  useEffect(() => {
    if (error && onError) onError(error);
  }, [error, onError]);

  if (!apiClients) return null;

  return (
    <div className={className ? `timeframe-sidebar ${className}` : 'timeframe-sidebar'}>
      {/* Mode toggle */}
      <div className="timeframe-sidebar__toggle">
        <ButtonAtom
          label={t('periodSelector.Nearby')}
          variant={mode === 'nearby' ? 'primary' : 'secondary'}
          size="small"
          shape="square-left"
          onClick={() => handleModeChange('nearby')}
        />
        <ButtonAtom
          label={t('periodSelector.All')}
          variant={mode === 'all' ? 'primary' : 'secondary'}
          size="small"
          shape="square-right"
          onClick={() => handleModeChange('all')}
        />
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div className="timeframe-sidebar__skeleton">
          {[1, 2, 3, 4].map((k) => (
            <Skeleton key={k} height="2rem" className="timeframe-sidebar__skeleton-item" />
          ))}
        </div>
      )}

      {/* Info: auto-fallback to All because nearby was empty */}
      {!loading && mode === 'all' && nearbyEmpty && (
        <div className="timeframe-sidebar__info">
          <i className="pi pi-info-circle" aria-hidden="true" />
          <span>{t('periodSelector.nearbyFallbackInfo')}</span>
        </div>
      )}

      {/* Empty: filtered to nothing */}
      {!loading && filtered.length === 0 && (
        <div className="timeframe-sidebar__empty">
          <span>{t('periodSelector.noMatchingTimeframes')}</span>
        </div>
      )}

      {/* Timeframe list */}
      {!loading && filtered.length > 0 && (
        <ul className="timeframe-sidebar__list" aria-label={t('periodSelector.timeframeListLabel')}>
          {filtered.map((item) => {
            const active = item.id === activeTimeframeId;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  aria-current={active ? 'true' : undefined}
                  className={`timeframe-sidebar__item${active ? ' timeframe-sidebar__item--active' : ''}`}
                  onClick={() => onSelect(item)}
                >
                  <span className="timeframe-sidebar__item-name">{item.name}</span>
                  {item.isOrganizationLevel && (
                    <span className="timeframe-sidebar__org-badge">
                      {t('periodSelector.orgBadge')}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default TimeframeSidebarMolecule;
